import { client } from '../../../dev-helpers/location/location.repo';
import { Location, SelectOption } from '../types';

declare global {
  interface Window {
    up_necessary?: {
      form_gyms?: {
        gyms?: {
          byName?: Record<string, Record<string, string>>;
        };
      };
    };
  }
}

export class LocationsService {
  static async fetchLocations(): Promise<Location[]> {
    try {
      const response = await client.get('/all');
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }
      const data = response.data;
      console.log('Fetched data:', data);
      return data.locations || [];
    } catch (error) {
      console.error('Error fetching locations from API:', error);
      return [];
    }
  }

  static processAPILocations(
    storeLocations: {
      location_name?: string;
      location_id?: string;
      address?: { city?: string; country?: string; region?: string };
      country_code?: string;
    }[]
  ): Location[] {
    return storeLocations.map((location) => {
      const region = this.extractRegion(location);
      const tags = region ? [`enquiry_form_${region}`] : [];

      return {
        location_name: location.location_name || '',
        location_id: location.location_id || '',
        address: {
          city: location.address?.city || '',
          country: location.address?.country || '',
          region: region || location.address?.region || ''
        },
        tags,
        country_code: location.country_code || ''
      };
    });
  }

  static processFallbackLocations(): Location[] {
    if (!window.up_necessary?.form_gyms?.gyms?.byName) {
      console.error('No fallback locations data available');
      return [];
    }

    try {
      return Object.entries(window.up_necessary.form_gyms.gyms.byName).flatMap(
        ([region, locations]) => {
          return Object.entries(locations).map(([locationId, locationName]) => ({
            location_name: locationName as string,
            location_id: locationId,
            address: {
              city: (locationName as string).split(' / ')[0],
              country: (locationName as string).split(' / ')[1] || '',
              region
            },
            tags: [`enquiry_form_${region}`],
            country_code: this.mapRegionToCountryCode(region)
          }));
        }
      );
    } catch (error) {
      console.error('Error processing fallback locations:', error);
      return [];
    }
  }

  private static extractRegion(location: {
    address?: { region?: string };
    location_name?: string;
  }): string {
    if (location.address?.region) {
      return location.address.region;
    }

    if (location.location_name?.includes(' / ')) {
      const parts = location.location_name.split(' / ');
      if (parts.length > 1) return parts[1];
    }

    if (location.location_name?.includes(' - ')) {
      const parts = location.location_name.split(' - ');
      if (parts.length > 1) {
        const knownRegions = [
          'United States',
          'United Kingdom',
          'Australia',
          'Singapore',
          'Hong Kong',
          'Dubai',
          'Netherlands'
        ];
        const secondPart = parts[1].trim();
        if (knownRegions.some((r) => secondPart.includes(r))) {
          return secondPart;
        }
      }
    }

    return '';
  }

  private static mapRegionToCountryCode(region: string): string {
    const regionMap: Record<string, string> = {
      'United States': 'US',
      'United Kingdom': 'UK',
      Australia: 'AU',
      Singapore: 'SG',
      'Hong Kong': 'HK',
      Dubai: 'AE',
      Netherlands: 'NL',
      'Middle East': 'AE',
      Australasia: 'AU',
      Europe: 'EU',
      Asia: 'AS'
    };
    return regionMap[region] || '';
  }

  static filterLocationsByRegion(locations: Location[], region: SelectOption | null): Location[] {
    if (!region || locations.length === 0) {
      return [];
    }

    // Try to filter by tags
    let filteredLocations = locations.filter((location) => {
      const matchTag = `enquiry_form_${region.key}`;
      return location.tags?.some((tag) => tag === matchTag);
    });

    // If no locations found by tags, try by region in address
    if (filteredLocations.length === 0) {
      filteredLocations = locations.filter((location) => {
        const locationRegion = location.address?.region;
        return locationRegion?.includes(String(region.key));
      });
    }

    // If still no locations found, try by location_name
    if (filteredLocations.length === 0) {
      filteredLocations = locations.filter((location) => {
        const locationName = String(location.location_name).toLowerCase();
        const regionName = String(region.key).toLowerCase();
        return locationName.includes(regionName);
      });
    }

    // If still no locations found, try by country code
    if (filteredLocations.length === 0) {
      const regionCountryCodes: Record<string, string[]> = {
        'United States': ['US', 'USA'],
        'United Kingdom': ['UK', 'GB'],
        Australia: ['AU', 'AUS'],
        Singapore: ['SG', 'SGP'],
        'Hong Kong': ['HK', 'HKG'],
        Dubai: ['AE', 'UAE'],
        Netherlands: ['NL', 'NLD'],
        'Middle East': ['AE', 'UAE'],
        Australasia: ['AU', 'AUS'],
        Europe: ['NL', 'NLD'],
        Asia: ['SG', 'SGP', 'HK', 'HKG']
      };

      const expectedCountryCodes = regionCountryCodes[region.key] || [];
      filteredLocations = locations.filter((location) => {
        const countryCode = location.country_code;
        return countryCode && expectedCountryCodes.includes(countryCode);
      });
    }

    return filteredLocations.length === 0 ? locations : filteredLocations;
  }
}
